/**
 * Hooks React Query da Tela 4 (Coleta Estruturada) — Brief 2B B6 (ADR-144).
 *
 * `useColetaDraft` faz get-or-create do rascunho ATIVO da conversa (GET 404 →
 * POST cria vazio). As mutations (save/finalize/cancel) mandam `If-Match: version`
 * e atualizam o cache com o draft fresco (version +1). 409 (OptimisticLock) é
 * propagado para o chamador refazer fetch + retry honesto.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api } from '../../lib/api-client';

import type { ColetaSchema, ColetaWritePayload, StructuredCollection } from './types';

function draftKey(conversationId: string): readonly unknown[] {
  return ['structured-collection', conversationId];
}

function schemaKey(draftId: string): readonly unknown[] {
  return ['structured-collection-schema', draftId];
}

/**
 * PR B (ADR-155 D9) — schema da tela derivado do Playbook da conversa.
 * Fetch GATED pelo draft carregado (memória `feedback_gatear_fetch_nao_so_render`).
 *
 * PR C (Important da revisão adversarial, ADR-155 D5) — `staleTime` deixou de
 * ser `Infinity`: o schema agora carrega `inherited`, que depende do EPISÓDIO
 * EM FOCO da conversa — a IA pode remarcar/bookar no MEIO da conversa
 * (mudando o foco) sem o operador reabrir a tela. 30s + refetch ao voltar o
 * foco da janela mantêm o herdado fresco; o form legado NÃO regride durante
 * o refetch (React Query preserva `query.data` do fetch anterior até a
 * resposta nova chegar) — a reconciliação em `ColetaPanel` cuida do que fazer
 * quando o `schema` novo chega (campo marcado cujo herdado sumiu/mudou).
 */
export function useColetaSchema(draftId: string | undefined): {
  schema: ColetaSchema | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: draftId !== undefined ? schemaKey(draftId) : ['structured-collection-schema'],
    queryFn: () => api.getStructuredCollectionSchema(draftId!),
    enabled: draftId !== undefined,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  return { schema: query.data, isLoading: query.isLoading, isError: query.isError };
}

/**
 * GET rascunho ativo; 404 → cria um rascunho vazio (idempotente p/ 1 user/tab).
 *
 * Item 363 F1: se o rascunho em cache está `awaiting_confirmation`, o fetch vai
 * POR ID — o GET ativo só devolve `in_progress`, e o get-or-create criaria um
 * rascunho NOVO por baixo do travado (matando o acompanhamento do desfecho).
 */
export async function fetchDraft(
  conversationId: string,
  cached: StructuredCollection | undefined,
): Promise<StructuredCollection> {
  if (cached?.status === 'awaiting_confirmation') {
    return api.getStructuredCollectionById(cached.id);
  }
  try {
    return await api.getActiveStructuredCollection(conversationId);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return api.createStructuredCollection(conversationId, {});
    }
    throw err;
  }
}

/** Item 363 F1 — poll de 15s APENAS enquanto aguarda a confirmação do titular. */
const AWAITING_POLL_MS = 15_000;

export function useColetaDraft(conversationId: string | undefined): {
  draft: StructuredCollection | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: conversationId !== undefined ? draftKey(conversationId) : ['structured-collection'],
    queryFn: () =>
      fetchDraft(conversationId!, qc.getQueryData<StructuredCollection>(draftKey(conversationId!))),
    enabled: conversationId !== undefined,
    staleTime: Infinity, // o rascunho é o documento de trabalho; refetch é manual/409/poll.
    retry: false,
    refetchInterval: (q) =>
      q.state.data?.status === 'awaiting_confirmation' ? AWAITING_POLL_MS : false,
  });
  return {
    draft: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}

export interface ColetaMutations {
  save: (payload: ColetaWritePayload) => Promise<StructuredCollection>;
  finalize: () => Promise<StructuredCollection>;
  cancel: () => Promise<StructuredCollection>;
  requestTitularConfirmation: () => Promise<StructuredCollection>;
  isSaving: boolean;
  isFinalizing: boolean;
  isCancelling: boolean;
  isRequestingConfirmation: boolean;
}

/**
 * Mutations da coleta. Cada uma usa a `version` atual do cache como If-Match e,
 * no sucesso, grava o draft fresco no cache (version +1). Em 409, o chamador
 * deve `refetch()` e tentar de novo.
 */
export function useColetaMutations(conversationId: string): ColetaMutations {
  const qc = useQueryClient();
  const key = draftKey(conversationId);

  const currentVersion = (): number => {
    const cached = qc.getQueryData<StructuredCollection>(key);
    return cached?.version ?? 0;
  };
  const currentId = (): string => {
    const cached = qc.getQueryData<StructuredCollection>(key);
    if (cached === undefined) throw new Error('Coleta ainda não carregada.');
    return cached.id;
  };
  const writeBack = (fresh: StructuredCollection): StructuredCollection => {
    qc.setQueryData(key, fresh);
    return fresh;
  };

  const save = useMutation({
    mutationFn: (payload: ColetaWritePayload) =>
      api.updateStructuredCollection(currentId(), currentVersion(), payload),
    onSuccess: writeBack,
  });
  const finalize = useMutation({
    mutationFn: () => api.finalizeStructuredCollection(currentId(), currentVersion()),
    onSuccess: writeBack,
  });
  const cancel = useMutation({
    mutationFn: () => api.cancelStructuredCollection(currentId(), currentVersion()),
    onSuccess: writeBack,
  });
  // Item 361 F2 — dispara a confirmação com o titular (trava o rascunho).
  const requestConfirmation = useMutation({
    mutationFn: () => api.requestTitularConfirmation(currentId(), currentVersion()),
    onSuccess: writeBack,
  });

  return {
    save: save.mutateAsync,
    finalize: finalize.mutateAsync,
    cancel: cancel.mutateAsync,
    requestTitularConfirmation: requestConfirmation.mutateAsync,
    isSaving: save.isPending,
    isFinalizing: finalize.isPending,
    isCancelling: cancel.isPending,
    isRequestingConfirmation: requestConfirmation.isPending,
  };
}
